import React, { useState } from 'react';
import { FaTimes, FaCheck, FaArrowLeft, FaArrowRight, FaFileAlt, FaEdit, FaListUl, FaSave, FaPencilAlt, FaInfoCircle } from 'react-icons/fa';
import Button from '../../components/buttons/Button';
import Input from '../../components/inputs/Input';
import FullScreenTextEditor from '../../components/editors/FullScreenTextEditor';
import '../../Templates/TemplateBuilder/TemplateBuilderWizard.css';
import { formatDisplayName } from '../../models/ReportTypeDesignHelper';

function CreateEnterpriseReportTypeWizard({
  isOpen,
  onClose,
  step,
  setStep,
  newName,
  setNewName,
  newCategory,
  setNewCategory,
  newDescription,
  setNewDescription,
  newInstructions,
  setNewInstructions,
  exampleProportion,
  setExampleProportion,
  creating,
  validateName,
  enterpriseShortName,
  onCreate,
  nameTouched,
  setNameTouched,
  existingNames = []
}) {
  const [showInstructionEditor, setShowInstructionEditor] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  
  // Validation helper function
  const isNameValid = (name) => {
    if (!name) return false;
    if (name.length < 3) return false;
    if (name.length > 50) return false;
    // Allow letters, spaces, basic latin accents
    return /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(name);
  };

  // Check if current step can proceed
  const canProceedFromStep = () => {
    if (step === 1) {
      return isNameValid(newName) && !nameExists;
    }
    if (step === 2) return !!newCategory;
    if (step === 3) return true; // description is optional
    if (step === 4) return true; // restrictions is always valid
    if (step === 5) return true; // instructions are optional
    return false;
  };
  
  if (!isOpen) return null;

  return (
    <div className="tbw-overlay" onClick={onClose}>
      <div className="tbw-modal" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tbw-header">
          <h2>Créer un modèle organisation</h2>
          <button className="tbw-close" onClick={onClose} aria-label="Fermer">
            <FaTimes size={18} />
          </button>
        </div>

      {/* Steps */}
      <div className="tbw-steps">
        {[
          { id: 1, title: 'Nom', icon: FaFileAlt },
          { id: 2, title: 'Catégorie', icon: FaEdit },
          { id: 3, title: 'Description', icon: FaListUl },
          { id: 4, title: 'Restrictions', icon: FaSave },
          { id: 5, title: 'Instructions', icon: FaPencilAlt }
        ].map((s) => {
          const Icon = s.icon;
          const accessible = s.id <= step;
          return (
            <div
              key={s.id}
              className={`tbw-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'completed' : ''} ${!accessible ? 'disabled' : ''}`}
              onClick={() => accessible && setStep(s.id)}
            >
              <div className="tbw-step-number">
                {step > s.id ? <FaCheck size={12} /> : <Icon size={16} />}
              </div>
              <div className="tbw-step-title">{s.title}</div>
            </div>
          );
        })}
        <div className="tbw-step-progress" style={{ width: `${((step - 1) / (5 - 1)) * 100}%` }} />
      </div>

      {/* Content */}
      <div className="tbw-content">
        {step === 1 && (
          <div className="-reporttype-enteprise-modal-form-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>Nom du modèle</span>
              <span style={{ fontSize: 12, color: '#666' }}>{newName.length}/50</span>
            </div>
            <Input
              id="enterprise-rt-name"
              type="text"
              value={newName}
              onChange={(e) => {
                const v = e.target.value;
                setNewName(v);
                if (nameTouched) setNameTouched(false);
                // Check if name exists in existing names
                const exists = (existingNames || []).some((n) => String(n).toLowerCase() === String(v).trim().toLowerCase());
                setNameExists(exists);
              }}
              onBlur={() => setNameTouched(true)}
              placeholder="Ex: consultation cardiologie"
              maxLength={50}
              required
            />
            {((newName && newName.length < 3) || newName.length > 50 || !/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(newName || '') || nameExists) && (
              <div style={{ color: '#c0392b', fontSize: 12, fontStyle: 'italic', marginTop: 6 }}>
                {[
                  newName && newName.length < 3 ? 'Au moins 3 caractères' : null,
                  newName && newName.length > 50 ? 'Maximum 50 caractères' : null,
                  newName && !/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(newName || '') ? 'Lettres uniquement' : null,
                  nameExists ? 'Ce nom existe déjà' : null,
                ].filter(Boolean).join(' • ')}
              </div>
            )}
            {isNameValid(newName) && newName.trim() && !nameExists && (
              <div className="-reporttype-enteprise-modal-preview" style={{ marginTop: 12, padding: 10, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                <span style={{ fontSize: 13, color: '#0369a1' }}>Aperçu pour les membres de l'organisation:</span>
                <br />
                <strong style={{ color: '#0c4a6e' }}>{formatDisplayName(newName) + ' ' + enterpriseShortName}</strong>
              </div>
            )}
            <div className="rtc-hint" style={{ marginTop: 8, color: '#666', fontSize: 12, fontStyle: 'italic' }}>
              Le nom affiché aux utilisateurs (ex. « Je dicte un rapport d'assurance »).
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="-reporttype-enteprise-modal-form-section">
            <label>
              <span className="-reporttype-enteprise-modal-label-text">Catégorie</span>
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['rapport_long','examen_technique','rapport_court','autre'].map((cat) => (
                <Button
                  key={cat}
                  variant={newCategory === cat ? 'primary' : 'secondary'}
                  isSelected={newCategory === cat}
                  type="fixed"
                  onClick={() => setNewCategory(cat)}
                >
                  {formatDisplayName(cat)}
                </Button>
              ))}
            </div>
            <div className="rtc-hint" style={{ marginTop: 6, color: '#666', fontSize: 12, fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaInfoCircle /> Sert à organiser et filtrer les modèles. Non visible à l'export.
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="-reporttype-enteprise-modal-form-section">
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 8 }}>
              Description
            </label>
            <div className="modern-textarea-wrapper">
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Décrivez brièvement ce modèle et son utilisation..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e1e5e9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  minHeight: '100px',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  outline: 'none',
                  background: '#ffffff'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e1e5e9';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            <div className="rtc-hint" style={{ marginTop: 8, color: '#666', fontSize: 12, fontStyle: 'italic' }}>
              Courte description visible aux utilisateurs (optionnel).
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="-reporttype-enteprise-modal-slider-section">
            <div className="-reporttype-enteprise-modal-slider-header">
              <label style={{ margin: 0 }}>
                <span className="-reporttype-enteprise-modal-label-text">Restrictions des utilisateurs</span>
              </label>
              <span className="-reporttype-enteprise-modal-slider-value">{exampleProportion}%</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              {[25, 50, 75, 100].map(value => (
                <button
                  key={value}
                  onClick={() => setExampleProportion(value)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    border: `2px solid ${exampleProportion === value ? '#960018' : '#e0e0e0'}`,
                    borderRadius: 8,
                    background: exampleProportion === value ? '#960018' : 'white',
                    color: exampleProportion === value ? 'white' : '#333',
                    fontWeight: exampleProportion === value ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontSize: 14
                  }}
                >
                  {value}%
                </button>
              ))}
            </div>
            <div className="-reporttype-enteprise-modal-helper-text" style={{ marginTop: 12 }}>
              Définissez le niveau de standardisation (25% libre → 100% rigide).
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="-reporttype-enteprise-modal-form-section">
            <label>
              <span className="-reporttype-enteprise-modal-label-text">Instructions personnalisées (optionnel)</span>
            </label>
            <div
              role="button"
              onClick={(e) => { e.stopPropagation(); setShowInstructionEditor(true); }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              tabIndex={0}
              style={{ padding: 14, border: '2px solid #e0e0e0', borderRadius: 10, background: '#fafafa', minHeight: 60, cursor: 'text' }}
              title={newInstructions || ''}
            >
              {newInstructions || <span style={{ color: '#999', fontStyle: 'italic' }}>Cliquez pour saisir des instructions…</span>}
            </div>
            <div className="rtc-hint" style={{ marginTop: 6, color: '#666', fontSize: 12, fontStyle: 'italic' }}>
              Consignes appliquées pour tous les utilisateurs de l'organisation.
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tbw-footer">
        <div className="tbw-footer-left">
          <Button variant="secondary" icon={FaArrowLeft} onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || creating}>
            Précédent
          </Button>
        </div>
        <div className="tbw-footer-center">Étape {step} sur 5</div>
        {step < 5 ? (
          <Button variant="primary" icon={FaArrowRight} onClick={() => setStep((s) => Math.min(5, s + 1))} disabled={creating || !canProceedFromStep()}>
            Suivant
          </Button>
        ) : (
          <Button type="fixed" onClick={onCreate} disabled={creating || !canProceedFromStep()} variant="primary">
            {creating ? 'Création...' : 'Créer le modèle'}
          </Button>
        )}
      </div>

      </div>
      
      {/* Full Screen Editor (outside modal to prevent click issues) */}
      {showInstructionEditor && (
        <div onClick={(e) => e.stopPropagation()}>
          <FullScreenTextEditor
            isOpen={showInstructionEditor}
            onClose={() => setShowInstructionEditor(false)}
            onSave={async (value) => { setNewInstructions(value); }}
            initialValue={newInstructions}
            title={'Instructions personnalisées'}
            placeholder="Entrez les instructions personnalisées..."
            saveButtonText="Utiliser ce texte"
          />
        </div>
      )}
    </div>
  );
}

export default CreateEnterpriseReportTypeWizard;


